import Alpine from 'alpinejs';

import { pushToDataLayer } from '../../../tracking.ts';

Alpine.data('app_view_components_layout_Expandable', () => ({
    isOpen: false,

    init() {
        this.onUpLocationChanged = this.onUpLocationChanged.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        // `$refs.panel` is bound via `x-bind="content"`, so it isn't available
        // synchronously in `init()`. Defer until the refs are wired up: seed
        // `isOpen` from the DOM, then start listening. Registering the listeners
        // only after the seed means the handlers can never observe a stale
        // `isOpen` or an undefined `$refs.panel`.
        this.$nextTick(() => {
            const panel = this.$refs.panel;

            // The browser may already have revealed the panel (find-in-page or a
            // deep link firing before Alpine booted), so seed from the DOM.
            this.isOpen = !panel.hasAttribute('hidden');

            // Already open before we booted means the browser revealed it for
            // a fragment in the initial URL. The panel was shown to the reader
            // either way, so it counts as opened.
            if (this.isOpen) {
                this.trackOpen('deep_link');
            }

            document.addEventListener(
                'up:location:changed',
                this.onUpLocationChanged,
            );
            // Capture phase: Unpoly's own click handling runs in the bubble
            // phase and halts the event (see `onDocumentClick`), so listen on
            // the way down to be sure we run first.
            document.addEventListener('click', this.onDocumentClick, true);
        });
    },

    destroy() {
        document.removeEventListener(
            'up:location:changed',
            this.onUpLocationChanged,
        );
        document.removeEventListener('click', this.onDocumentClick, true);
    },

    // `hidden="until-found"` keeps the collapsed panel out of the tab order and
    // the accessibility tree while leaving it findable by the browser. Reveal it,
    // then animate max-height from 0 up to the content height.
    expand() {
        const panel = this.$refs.panel;

        this.isOpen = true;
        panel.style.maxHeight = '0px';
        panel.removeAttribute('hidden');
        panel.style.maxHeight = `${panel.scrollHeight}px`;
    },

    // Animate max-height down to 0; the transitionend handler re-hides it.
    collapse() {
        const panel = this.$refs.panel;

        this.isOpen = false;
        panel.style.maxHeight = `${panel.scrollHeight}px`;
        void panel.offsetHeight;
        panel.style.maxHeight = '0px';
    },

    // A panel can be opened four ways, and the thing we want to count is that
    // it was opened at all — so they share one event and record how as a
    // parameter. Collapsing is not tracked; the question is whether the
    // content was seen.
    trackOpen(trigger: string) {
        pushToDataLayer({
            event: 'expandable_open',
            click_text: this.titleText(),
            trigger,
        });
    },

    // The trigger's text, minus the chevron and the visually hidden "Visa
    // innehåll" label — i.e. the heading the reader actually sees.
    titleText(): string | null {
        const button = this.$refs.button;

        if (!button) {
            return null;
        }

        let text = '';

        button.childNodes.forEach((node: Node) => {
            if (node instanceof Element && node.matches('svg, .sr-only')) {
                return;
            }

            text += node.textContent ?? '';
        });

        return text.trim() || null;
    },

    onButtonClick() {
        if (this.isOpen) {
            this.collapse();
        } else {
            this.expand();
            this.trackOpen('button');
        }
    },

    // The browser revealed the panel for a find-in-page match; sync the trigger
    // and leave it isOpen (the reveal itself is instant).
    onBeforeMatch() {
        this.isOpen = true;
        this.$refs.panel.style.maxHeight = '';
        this.trackOpen('find_in_page');
    },

    // Native fragment navigation (initial load, a programmatic
    // `location.hash =`, Ctrl+F) reveals our `hidden="until-found"` panel
    // when the target id lives inside it: the browser strips `hidden` and
    // fires `beforematch` above. But Unpoly hijacks same-page anchor clicks
    // via `up.on('up:click', 'a[href*="#"]', ...)`, halting the event (so
    // the browser's reveal-and-scroll-to-fragment algorithm never runs) and
    // scrolling through its own viewport logic, which leaves
    // hidden-until-found content hidden. So a click on a link to our anchor
    // updates the URL but leaves us collapsed. Unpoly still announces the
    // change with `up:location:changed`, so backfill from there: if the new
    // hash points at an element inside our still-collapsed panel, expand it
    // ourselves. The `isOpen` guard (in `revealHashIfInsidePanel`) skips the
    // native paths, which the browser has already revealed.
    onUpLocationChanged() {
        this.revealHashIfInsidePanel(window.location.hash, 'location_change');
    },

    // `up:location:changed` only fires when the location actually changes, so
    // it misses re-clicking a link to the hash we're already on (e.g. open via
    // `#our-anchor`, collapse, then click an `<a href="#our-anchor">`): the URL
    // is unchanged, Unpoly emits nothing, and — having halted the click — the
    // browser never runs its native `hidden=until-found` reveal either. Catch
    // the click directly. We run in the capture phase, ahead of the bubble-
    // phase handler where Unpoly halts these clicks, so the event still reaches
    // us. Scope to same-document fragment links — the navigation the browser
    // would otherwise have revealed natively.
    onDocumentClick(e: MouseEvent) {
        const eventTarget = e.target;

        if (!(eventTarget instanceof Element)) {
            return;
        }

        const anchor = eventTarget.closest('a[href]');

        if (!(anchor instanceof HTMLAnchorElement)) {
            return;
        }

        if (
            anchor.origin !== window.location.origin ||
            anchor.pathname !== window.location.pathname ||
            anchor.search !== window.location.search
        ) {
            return;
        }

        this.revealHashIfInsidePanel(anchor.hash, 'anchor_link');
    },

    // If `hash` (`#id`) points at an element inside our still-collapsed panel,
    // expand it. The `isOpen` guard skips the native reveal paths, which the
    // browser has already opened for us.
    revealHashIfInsidePanel(hash: string, trigger: string) {
        if (this.isOpen) {
            return;
        }

        if (hash.length < 2) {
            return;
        }

        let targetId;
        try {
            targetId = decodeURIComponent(hash.slice(1));
        } catch {
            return;
        }

        const target = document.getElementById(targetId);

        if (!target) {
            return;
        }

        const panel = this.$refs.panel;

        if (!panel.contains(target)) {
            return;
        }

        this.expand();
        this.trackOpen(trigger);
    },

    onTransitionEnd(e: TransitionEvent) {
        const panel = this.$refs.panel;

        if (e.target !== panel || e.propertyName !== 'max-height') {
            return;
        }
        // Drop the cap once expanded so the panel can reflow; once collapsed,
        // re-hide it for the tab order, the a11y tree and find-in-page.
        panel.style.maxHeight = '';
        if (!this.isOpen) {
            panel.setAttribute('hidden', 'until-found');
        }
    },

    button: {
        // Bound here rather than in the PHP so the markup — and the
        // Expandable visual snapshots — stay untouched.
        'x-ref': 'button',
        ':aria-expanded': 'isOpen ? "true" : "false"',
        '@click': 'onButtonClick',
    },

    content: {
        'x-ref': 'panel',
        '@beforematch': 'onBeforeMatch',
        '@transitionend': 'onTransitionEnd',
    },
}));
